import Sentry from '../Sentry'

import { BayGridSize, bayManager } from './manager'
import { BayMachineList, RawBayMachineList } from './types'

import { SOCKET_MESSAGES } from '.'


class BaySocketEvent {
  constructor() {
  }

  _EVENT_KEY?: string

  get EVENT_KEY() {
    return this._EVENT_KEY
  }

  isEventResponseFail(status: string): boolean {
    return status !== 'SUCCESS'
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trigger(..._args: unknown[]) {
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  listener(..._args: unknown[]) {
  }

  logger(status: string, response: unknown) {
    console.log(`<${this.EVENT_KEY}>`, status, response)

    if (this.isEventResponseFail(status)) {
      Sentry.withScope((scope) => {
        scope.setTag('protocol', 'socket')
        scope.setLevel('warning')
        scope.setContext('Fail Response', {
          EventKey: this.EVENT_KEY,
          Status: status,
          Response: response,
        })

        Sentry.captureMessage(
          `Socket Fail Response <${this.EVENT_KEY}>`
        )
      })
    }
  }
}

class DisconnectEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.DISCONNECTED

  listener = (): void => {
    console.log('타석과 연결이 끊어졌습니다.')
  }
}

class GridSizeEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.GRID_SIZE

  listener = (
    status: string,
    content: { gridWidth: number; gridHeight: number }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const bayGridSize = new BayGridSize()
    bayGridSize.width = content.gridWidth
    bayGridSize.height = content.gridHeight

    bayManager.gridSizeSetter?.(bayGridSize)
  }
}

class AllMachineEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.ALL_MACHINES

  listener = (status: string, content: RawBayMachineList): void => {
    if (this.isEventResponseFail(status)) return

    const machines = Object.values(content) as BayMachineList

    bayManager.setMachines(machines)
  }
}

class ServerTimeEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.SERVER_TIME
  shopID?: string | number

  trigger() {
    return {
      shopID: this.shopID,
    }
  }

  listener = (status: string, {serverTime}: { serverTime: Date }): void => {
    if (this.isEventResponseFail(status)) return

    const server = new Date(serverTime).getTime()
    const client = new Date().getTime()

    bayManager.serverTimeDiff = server - client
  }
}

class UpdateGridCoordsEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.UPDATE_GRID_COORDS

  listener = (
    status: string,
    response: {
      machineID: number
      x: number
      y: number
    }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          gridX: response.x,
          gridY: response.y,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

class MachineAuthenticateEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.AUTHENTICATE

  listener = (
    status: string,
    response: {
      gridX: number
      gridY: number
      machineID: number
      machineName: string
      sensorState: string
      serialNumber: string
      serverKey: string
      shopID: number
      socketID: string
    }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          ...response,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

class MachineDisconnectEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.DISCONNECTED_MACHINE

  listener = (status: string, response: { machineID: number }): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          userID: undefined,
          socketID: undefined,
          startDate: undefined,
          endDate: undefined,
          timer: undefined,
          sensorState: undefined,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

class MachineLoginEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.LOGIN_BY_RMS

  listener = (
    status: string,
    response: {
      machineID: number
      sensorState: string
      shopID: number
      shopMemberID: number
      userAccount: string
      userID: number
      userItemID: number
      userItemName: string
      userName: string
      userNickName: string
      startDate?: string
      endDate?: string
    }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          userID: response.userID,
          userNickName: response.userNickName,
          userName: response.userName,
          userItemName: response.userItemName,
          shopMemberID: response.shopMemberID,
          startDate: response.startDate,
          endDate: response.endDate,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

class MachineLogoutEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.LOGOUT_BY_RMS

  listener = (status: string, response: { userID: number }): void => {
    console.log(`<${this._EVENT_KEY}>`, status, response)

    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.userID == response.userID) {
        return {
          ...machine,
          shopMemberID: undefined,
          userID: undefined,
          userItemName: undefined,
          userName: undefined,
          userNickName: undefined,
          startDate: undefined,
          endDate: undefined,
          timer: undefined,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

class MachineStartPraticeEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.START_PRACTICE

  listener = (
    status: string,
    response: {
      machineID: number
      startDate: string
      endDate: string
    }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          startDate: response.startDate,
          endDate: response.endDate,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

// class MachineEndPracticeEvent extends BaySocketEvent {
//     _EVENT_KEY = SOCKET_MESSAGES.END_PRACTICE

//     listener = (status: string, response: unknown): void {
//         // 머신아이디를 안주는 것 같음
//         console.log(response)
//     }
// }

class MachineAddTimeEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.ADD_TIME

  listener = (
    status: string,
    response: {
      machineID: number
      startDate: string
      endDate: string
    }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          startDate: response.startDate,
          endDate: response.endDate,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

class RefreshLoginCodeEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.REFRESH_LOGIN_CODE

  listener = (
    status: string,
    response: {
      machineID: number
      loginCode: number
    }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          loginCode: response.loginCode,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

class SmartLoginStateEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.SMART_LOGIN_STATE

  listener = (
    status: string,
    response: { machineID: number; state: 0 | 1 }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          smartLoginState: response.state,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

class LoginCodeEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.REFRESH_LOGIN_CODE

  listener = (
    status: string,
    response: { machineID: number; loginCode: number }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          loginCode: response.loginCode,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

class MachineSensorEvent extends BaySocketEvent {
  _EVENT_KEY = SOCKET_MESSAGES.SENSOR_STATE

  listener = (
    status: string,
    response: {
      machineID: number
      sensorState: string
    }
  ): void => {
    if (this.isEventResponseFail(status)) return

    const updated = bayManager.machines.map((machine) => {
      if (machine.machineID == response.machineID) {
        return {
          ...machine,
          sensorState: response.sensorState,
        }
      }

      return machine
    })

    bayManager.setMachines(updated)
  }
}

export type BaySocketEventClass = BaySocketEvent

export default {
  DisconnectEvent,
  GridSizeEvent,
  AllMachineEvent,
  ServerTimeEvent,
  UpdateGridCoordsEvent,
  MachineAuthenticateEvent,
  MachineDisconnectEvent,
  MachineLoginEvent,
  MachineLogoutEvent,
  MachineStartPraticeEvent,
  MachineAddTimeEvent,
  RefreshLoginCodeEvent,
  SmartLoginStateEvent,
  LoginCodeEvent,
  MachineSensorEvent,
}
